import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useHousehold } from "@/context/HouseholdContext";
import { api, formatApiError } from "@/lib/api";
import { t } from "@/i18n";

const ROLES = ["owner", "admin", "adult", "child", "guest"];

const RoleSelect = ({ value, onChange, testid, allowOwner }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-36 rounded-full text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      {ROLES.filter((r) => allowOwner || r !== "owner").map((r) => <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const CreateHouseholdDialog = () => {
  const { reload } = useAuth();
  const { select } = useHousehold();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/households", { name });
      await reload();
      select(data.id);
      setOpen(false);
      setName("");
      toast.success("Foyer créé");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full" data-testid="create-household-button"><Plus className="mr-2 h-4 w-4" />Nouveau foyer</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Créer un foyer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label htmlFor="hh-name">Nom du foyer</Label><Input id="hh-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Résidence principale" required minLength={2} data-testid="create-household-name-input" /></div>
          <Button type="submit" className="w-full rounded-full" data-testid="create-household-submit">Créer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const InviteForm = ({ hid, onDone }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("adult");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/households/${hid}/members`, { email, role });
      setEmail("");
      toast.success("Membre ajouté");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row" data-testid="invite-member-form">
      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemple.fr" required className="rounded-full" data-testid="invite-member-email-input" />
      <RoleSelect value={role} onChange={setRole} testid="invite-member-role-select" />
      <Button type="submit" className="rounded-full" data-testid="invite-member-submit"><UserPlus className="mr-2 h-4 w-4" />Ajouter</Button>
    </form>
  );
};

export const MembersPanel = () => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["household", active.id],
    queryFn: () => api.get(`/households/${active.id}`).then((r) => r.data),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["household", active.id] });
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  if (isLoading || !data) return <Skeleton className="h-40 rounded-2xl" />;
  const manager = ["owner", "admin"].includes(data.my_role);

  return (
    <section className="card-surface p-5 sm:p-6" data-testid="household-members-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Membres</h2>
        <span className="text-xs text-muted-foreground">Votre rôle : {t(`roles.${data.my_role}`)}</span>
      </div>
      <ul className="mt-4 divide-y divide-border">
        {data.members.map((m) => (
          <li key={m.user_id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`member-row-${m.user_id}`}>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-mint/50 text-xs font-bold text-navy">{m.name?.[0]}</div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{m.name}{m.is_me && " (vous)"}</div>
              {m.email && <div className="truncate text-xs text-muted-foreground">{m.email}</div>}
            </div>
            {manager && !m.is_me ? (
              <>
                <RoleSelect value={m.role} allowOwner={data.my_role === "owner"} testid={`member-role-select-${m.user_id}`}
                  onChange={(role) => act(() => api.patch(`/households/${active.id}/members/${m.user_id}`, { role }), "Rôle mis à jour")} />
                <Button variant="ghost" size="icon" aria-label="Retirer" data-testid={`member-remove-${m.user_id}`}
                  onClick={() => act(() => api.delete(`/households/${active.id}/members/${m.user_id}`), "Membre retiré")}><Trash2 className="h-4 w-4" /></Button>
              </>
            ) : <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium">{t(`roles.${m.role}`)}</span>}
          </li>
        ))}
      </ul>
      {manager && <div className="mt-4 border-t border-border pt-4"><InviteForm hid={active.id} onDone={refresh} /></div>}
      <p className="mt-4 text-xs text-muted-foreground">Les finances personnelles de chaque adulte restent privées, quel que soit son rôle dans le foyer.</p>
    </section>
  );
};
